import React from "react";
import { HOME } from "@/constants/testIds";
import { Ship, FileText, ClipboardCheck, AlertTriangle, Anchor, Truck, MapPin } from "lucide-react";

export const LiveDashboard = () => {
  return (
    <section
      id="dashboard"
      data-testid={HOME.dashboardSection}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-50" />

      <div className="container-narrow relative">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] ink-soft mb-4">
            03 — Live Dashboard
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-semibold ink leading-[1.05] text-balance">
            Every shipment, every detail — one screen.
          </h2>
          <p className="mt-5 text-[15px] ink-soft leading-relaxed">
            The Workspace interface is built around the shipment, not the carrier.
            Maps, milestones, ETAs, carrier feeds, documents, alerts and POs — all
            connected, never cluttered.
          </p>
        </div>

        <div className="glass rounded-[24px] p-3 md:p-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2 text-[12px] ink">
              <span className="font-display font-semibold">Workspace</span>
              <span className="ink-soft">/</span>
              <span className="ink-soft">Tracking</span>
              <span className="ink-soft">/</span>
              <span className="font-mono">All Shipments</span>
            </div>
            <div className="flex items-center gap-2">
              <Pill label="At Sea" tone="emerald" />
              <Pill label="Loading" tone="amber" />
              <Pill label="Delivered" tone="ink" />
            </div>
          </div>

          {/* Grid */}
          <div className="grid lg:grid-cols-12 gap-3 mt-2">
            {/* Map */}
            <div className="lg:col-span-7 rounded-2xl bg-white border border-ink-10 p-4 relative min-h-[320px]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10.5px] font-mono uppercase ink-soft tracking-widest">Map</div>
                <div className="text-[10.5px] font-mono ink-soft">3 shipments active</div>
              </div>
              <MapMini />
            </div>

            {/* Right column */}
            <div className="lg:col-span-5 space-y-3">
              <DataCard icon={Ship} label="Shipment" value="SH-2481 · MSCU1234567" sub="MSC · ETA 12 June" />
              <DataCard icon={FileText} label="Documents" value="3 attached · 0 missing" sub="BL · COO · Insurance" />
              <DataCard icon={ClipboardCheck} label="Inspection" value="Cleared at origin" sub="Quality A · 23 May" />
              <DataCard icon={AlertTriangle} label="Alerts" value="ETA shifted +1d" sub="Port congestion · Mombasa" tone="amber" />
            </div>

            {/* Timeline */}
            <div className="lg:col-span-12 rounded-2xl bg-white border border-ink-10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10.5px] font-mono uppercase ink-soft tracking-widest">Timeline</div>
                <div className="text-[10.5px] font-mono ink-soft">Updated 2 min ago</div>
              </div>
              <Timeline />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pill = ({ label, tone = "ink" }) => {
  const styles = {
    emerald: "bg-emerald/10 text-emerald border-[hsla(217,71%,35%,0.25)]",
    amber:   "bg-[hsla(38,92%,50%,0.10)] text-[hsl(38,92%,38%)] border-[hsla(38,92%,50%,0.25)]",
    ink:     "bg-ink-3 ink-soft border-ink-15",
  }[tone];
  return (
    <span className={`rounded-full text-[10.5px] font-mono px-2 py-1 border ${styles}`}>
      {label}
    </span>
  );
};

const DataCard = ({ icon: Icon, label, value, sub, tone = "ink" }) => (
  <div className="rounded-xl bg-white border border-ink-10 p-3.5 flex items-start gap-3 hover:border-[hsla(217,71%,35%,0.25)] transition-colors">
    <span className={`grid h-9 w-9 place-items-center rounded-lg ${tone === "amber" ? "bg-[hsla(38,92%,50%,0.10)] text-[hsl(38,92%,38%)]" : "bg-ink-3 ink"} shrink-0`}>
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[10.5px] font-mono uppercase ink-soft tracking-widest">{label}</div>
      <div className="text-[13px] font-medium ink truncate">{value}</div>
      <div className="text-[11.5px] ink-soft mt-0.5 truncate">{sub}</div>
    </div>
  </div>
);

const MapMini = () => (
  <svg viewBox="0 0 600 320" className="w-full h-[260px]" aria-hidden>
    <defs>
      <pattern id="dotsMini" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1" fill="hsla(222,47%,7%,0.12)" />
      </pattern>
      <linearGradient id="r2" x1="0" x2="1">
        <stop offset="0%" stopColor="hsl(var(--emerald))" />
        <stop offset="100%" stopColor="hsl(var(--emerald-bright))" />
      </linearGradient>
    </defs>
    <g fill="url(#dotsMini)">
      <path d="M70 90 q40 -30 130 -10 q60 15 80 60 q-10 50 -100 60 q-90 5 -130 -20 q-30 -20 20 -90 z" />
      <path d="M260 80 q60 -20 130 -5 q60 15 70 70 q-10 55 -110 65 q-90 10 -120 -20 q-30 -25 30 -110 z" />
      <path d="M310 180 q40 -20 80 0 q40 30 30 90 q-10 60 -70 60 q-60 -5 -60 -80 q0 -30 20 -70 z" />
    </g>
    <path d="M150 130 C 230 110, 290 150, 360 200" fill="none" stroke="url(#r2)" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="5 5" />
    <circle cx="150" cy="130" r="4" fill="white" stroke="hsl(var(--ink))" strokeWidth="1.5" />
    <circle cx="360" cy="200" r="5" fill="hsl(var(--emerald))" />
    <circle cx="360" cy="200" r="10" fill="none" stroke="hsl(var(--emerald))" strokeWidth="1.5" opacity="0.5" />
    <text x="160" y="125" fontSize="10" fill="hsl(var(--ink))" fontFamily="JetBrains Mono">Mersin</text>
    <text x="370" y="195" fontSize="10" fill="hsl(var(--ink))" fontFamily="JetBrains Mono">Mombasa</text>
  </svg>
);

const Timeline = () => {
  const steps = [
    { icon: FileText, label: "PO created",    time: "01 May", done: true },
    { icon: ClipboardCheck, label: "Inspection cleared", time: "20 May", done: true },
    { icon: Anchor,   label: "Container loaded", time: "23 May", done: true },
    { icon: Ship,     label: "Vessel departed", time: "26 May", done: true },
    { icon: MapPin,   label: "At sea — Med",    time: "Now",     current: true },
    { icon: Anchor,   label: "Mombasa arrival", time: "12 Jun", done: false },
    { icon: Truck,    label: "Delivered",       time: "15 Jun",  done: false },
  ];
  return (
    <div className="relative">
      <div className="absolute top-4 left-0 right-0 h-px bg-ink-5" />
      <div className="absolute top-4 left-0 h-px bg-gradient-to-r from-[hsl(var(--emerald))] to-[hsl(var(--emerald-bright))]" style={{ width: "58%" }} />
      <div className="relative grid grid-cols-7 gap-2">
        {steps.map((s) => {
          const I = s.icon;
          return (
            <div key={s.label} className="flex flex-col items-center text-center">
              <span
                className={`grid h-8 w-8 place-items-center rounded-full border ${
                  s.done
                    ? "bg-emerald text-white border-[hsl(var(--emerald))]"
                    : s.current
                    ? "bg-white text-emerald border-[hsl(var(--emerald))] ring-emerald"
                    : "bg-white text-ink-soft border-ink-15"
                }`}
              >
                <I className="h-3.5 w-3.5" />
              </span>
              <span className={`mt-2 text-[10.5px] font-medium ${s.current ? "ink" : "ink-soft"}`}>{s.label}</span>
              <span className="text-[10px] ink-soft font-mono">{s.time}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
